import React from 'react';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import SegmentedControl from '@cloudscape-design/components/segmented-control';

import { IgAttributionRow } from '../api/IgApi';
import {
  buildModel,
  rdbuColor,
  tkey,
  RDBU_STOPS,
  Metric,
} from '../utils/attributionHeatmap';

interface AttributionHeatmapProps {
  rows: IgAttributionRow[];
  /** Which attribution score to color cells by. Defaults to p50. */
  metric?: Metric;
  /**
   * Optional cap on feature rows (by summed |attribution|). Default: show ALL
   * features present in the data so the heatmap stays consistent with the table
   * regardless of the baseline (e.g. when static features become non-zero).
   */
  maxFeatures?: number;
  /**
   * Forecast Creation Date (YYYY-MM-DD). Columns with date < fcd are history,
   * date >= fcd are future; the FCD divider is drawn at that boundary. This is
   * the authoritative history/future split (matches the notebook's LENGTH_HISTORY).
   */
  fcd?: string;
}

const MARGIN = { top: 16, right: 72, bottom: 64, left: 230 };
const ROW_H = 22;
const PLOT_W = 900; // width of the temporal (day) column area, CSS px
const STATIC_COL_W = 64; // width of the static-feature column
const COL_GAP = 12; // gap between the static column and the temporal area

function ColorBar({
  vmax,
  metricLabel,
  perRow,
}: {
  vmax: number;
  metricLabel: string;
  perRow: boolean;
}): JSX.Element {
  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Box variant="small" color="text-body-secondary">
        {perRow ? '−peak' : `−${vmax.toFixed(3)}`}
      </Box>
      <div
        aria-hidden="true"
        style={{
          width: 200,
          height: 12,
          borderRadius: 2,
          border: '1px solid #d5dbdb',
          background:
            'linear-gradient(to right,' +
            RDBU_STOPS.map(
              ([t, c]) => `rgb(${c.join(',')}) ${((t + 1) / 2) * 100}%`
            ).join(',') +
            ')',
        }}
      />
      <Box variant="small" color="text-body-secondary">
        {perRow ? '+peak' : `+${vmax.toFixed(3)}`}
      </Box>
      <Box variant="small" color="text-body-secondary">
        {perRow
          ? 'Attribution — each row scaled to its own ±peak (shown next to each feature)'
          : `Attribution (contribution to ${metricLabel})`}
      </Box>
    </SpaceBetween>
  );
}

export default function AttributionHeatmap({
  rows,
  metric = 'p50_score',
  maxFeatures,
  fcd,
}: AttributionHeatmapProps): JSX.Element {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [scaleMode, setScaleMode] = React.useState<'shared' | 'row'>('shared');
  const model = React.useMemo(
    () => buildModel(rows, metric, fcd, maxFeatures),
    [rows, metric, fcd, maxFeatures]
  );

  const metricLabel = metric === 'p90_score' ? 'P90' : 'P50';
  const nTime = model.offsets.length;
  const nFeat = model.features.length;
  const hasStatic = model.hasStatic;

  // Color scale per cell: shared global vmax, or the cell's own feature (row) vmax.
  const cellVmax = React.useCallback(
    (featName: string) =>
      scaleMode === 'row' ? model.rowVmax.get(featName) ?? model.vmax : model.vmax,
    [scaleMode, model]
  );

  // Geometry. Optional static column on the left, then the temporal day area.
  const staticX = MARGIN.left;
  const dayAreaX = MARGIN.left + (hasStatic ? STATIC_COL_W + COL_GAP : 0);
  const dayAreaW = nTime > 0 ? PLOT_W : 0;
  const cellW = nTime > 0 ? PLOT_W / nTime : 0;
  const plotH = nFeat * ROW_H;
  const cssW = dayAreaX + dayAreaW + MARGIN.right;
  const cssH = MARGIN.top + plotH + MARGIN.bottom;

  const empty = nFeat === 0 || (nTime === 0 && !hasStatic);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || empty) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    const perRow = scaleMode === 'row';

    for (let i = 0; i < nFeat; i++) {
      const feat = model.features[i];
      const y = MARGIN.top + i * ROW_H;
      const vm = cellVmax(feat.name);

      // Static column cell.
      if (hasStatic) {
        if (feat.isStatic) {
          const v = model.staticAt.get(feat.name);
          ctx.fillStyle = v == null ? '#f4f4f4' : rdbuColor(v, vm);
          ctx.fillRect(staticX, y, STATIC_COL_W, ROW_H);
        } else {
          // N/A for temporal features — leave white.
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(staticX, y, STATIC_COL_W, ROW_H);
        }
      }

      // Temporal day cells.
      if (nTime > 0) {
        for (let j = 0; j < nTime; j++) {
          const x = dayAreaX + j * cellW;
          if (feat.isStatic) {
            ctx.fillStyle = '#ffffff'; // N/A for static features
          } else {
            const v = model.temporalAt.get(tkey(feat.name, model.offsets[j]));
            ctx.fillStyle = v == null ? '#f4f4f4' : rdbuColor(v, vm);
          }
          ctx.fillRect(x, y, Math.ceil(cellW) + 1, ROW_H);
        }
      }
    }

    ctx.strokeStyle = '#879596';
    ctx.lineWidth = 1;
    if (hasStatic) ctx.strokeRect(staticX, MARGIN.top, STATIC_COL_W, plotH);
    if (nTime > 0) ctx.strokeRect(dayAreaX, MARGIN.top, dayAreaW, plotH);

    // FCD boundary (dashed vertical line) within the temporal area.
    if (nTime > 0 && model.fcdIndex > 0) {
      const x = dayAreaX + model.fcdIndex * cellW;
      ctx.save();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.moveTo(x, MARGIN.top);
      ctx.lineTo(x, MARGIN.top + plotH);
      ctx.stroke();
      ctx.restore();
      ctx.fillStyle = '#000';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('FCD', x + 3, MARGIN.top + 10);
    }

    // Feature labels (y ticks). In per-feature mode, append each row's own peak
    // so the (non-comparable) scales are explicit.
    ctx.fillStyle = '#16191f';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < nFeat; i++) {
      const y = MARGIN.top + i * ROW_H + ROW_H / 2;
      const name = model.features[i].name;
      let label = name;
      if (label.length > 30) label = label.slice(0, 29) + '…';
      if (perRow) {
        const pk = model.rowVmax.get(name) ?? 0;
        label += `  (±${pk >= 1 ? pk.toFixed(1) : pk.toPrecision(2)})`;
      }
      ctx.fillText(label, MARGIN.left - 6, y);
    }

    // "Static" column header label (below the column).
    if (hasStatic) {
      ctx.fillStyle = '#16191f';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText('Static', staticX + STATIC_COL_W / 2, MARGIN.top + plotH + 6);
    }

    // Date ticks (x axis) for the temporal area, ~10 evenly spaced.
    if (nTime > 0) {
      ctx.fillStyle = '#16191f';
      ctx.font = '10px sans-serif';
      const nTicks = Math.min(10, nTime);
      for (let k = 0; k < nTicks; k++) {
        const j = nTicks === 1 ? 0 : Math.round((k / (nTicks - 1)) * (nTime - 1));
        const offset = model.offsets[j];
        const date = model.offsetDate.get(offset);
        const label = date ? String(date).slice(0, 10) : String(offset);
        const x = dayAreaX + j * cellW + cellW / 2;
        ctx.strokeStyle = '#879596';
        ctx.beginPath();
        ctx.moveTo(x, MARGIN.top + plotH);
        ctx.lineTo(x, MARGIN.top + plotH + 4);
        ctx.stroke();
        ctx.save();
        ctx.translate(x, MARGIN.top + plotH + 6);
        ctx.rotate(-Math.PI / 4);
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, 0, 0);
        ctx.restore();
      }

      // X axis title.
      ctx.fillStyle = '#5f6b7a';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText('Time step (day index)', dayAreaX + PLOT_W / 2, cssH - 2);
    }
  }, [model, nTime, nFeat, hasStatic, cellW, dayAreaX, dayAreaW, plotH, cssW, cssH, staticX, empty, cellVmax, scaleMode]);

  if (empty) {
    return <Box textAlign="center">No data</Box>;
  }

  const perRow = scaleMode === 'row';

  return (
    <SpaceBetween size="s">
      <Box variant="small" color="text-body-secondary">
        {metricLabel} attribution heatmap — {nFeat} feature
        {nFeat === 1 ? '' : 's'}. Rows = features; temporal features span time
        step (history → future, split at the FCD line)
        {hasStatic ? ', static features occupy the "Static" column' : ''}. Blue =
        negative, red = positive.{' '}
        {perRow
          ? 'Each row is scaled to its own peak — the value next to each feature is that row’s ± peak (magnitudes not comparable across rows).'
          : 'Shared scale, clipped at the 99th percentile (a dominant feature can wash out the rest).'}{' '}
        Shows the same features as the Table (excluding the reconstruction summary rows).
      </Box>
      <SpaceBetween direction="horizontal" size="s">
        <SegmentedControl
          selectedId={scaleMode}
          onChange={({ detail }) => setScaleMode(detail.selectedId as 'shared' | 'row')}
          label="Color scale"
          options={[
            { id: 'shared', text: 'Shared scale' },
            { id: 'row', text: 'Per-feature' },
          ]}
        />
        <ColorBar vmax={model.vmax} metricLabel={metricLabel} perRow={perRow} />
      </SpaceBetween>
      <div style={{ overflowX: 'auto' }}>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`${metricLabel} feature attribution heatmap`}
        />
      </div>
    </SpaceBetween>
  );
}
